import * as XLSX from "xlsx";
import React, { useEffect, useState } from "react";
import { Button } from "../../components/Common/AppOverlay";
import { engagementFields } from "./engagementMapping";

const normalized = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
const coreKeys = [
  "engagementName",
  "clientName",
  "workType",
  "financialYearEnd",
  "industry",
  "partner",
];

export const sampleText = (value, field) => {
  const serial =
    typeof value === "number"
      ? value
      : /^\d{5}(\.\d+)?$/.test(String(value))
      ? Number(value)
      : NaN;
  if (
    Number.isFinite(serial) &&
    ["financialYearEnd", "dueDate"].includes(field)
  ) {
    const date = XLSX.SSF.parse_date_code(serial);
    if (date && serial >= 1 && serial < 2958466)
      return `${String(date.d).padStart(2, "0")}/${String(date.m).padStart(
        2,
        "0"
      )}/${date.y}`;
  }
  return value instanceof Date
    ? value.toLocaleDateString("en-GB")
    : String(value ?? "");
};

export default function EngagementMappingReview({
  headers,
  mappings,
  samples = {},
  onChange,
  warnings,
  aiUsed,
  confirmed,
  onConfirm,
  onContinue,
  onBack,
  onReset,
  canContinue,
}) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  const [reviewed, setReviewed] = useState({});
  useEffect(() => {
    setReviewed((current) =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([key, source]) => mappings[key] === source
        )
      )
    );
  }, [mappings]);
  const used = Object.values(mappings).filter(Boolean);
  const excluded = headers.filter((header) => !used.includes(header));

  const isRequired = (key) =>
    ["engagementName", "workType", "clientName"].includes(key) &&
    !(key === "clientName" && mappings.clientCode);

  const statusFor = ([key, label]) =>
    !mappings[key]
      ? isRequired(key)
        ? "Missing"
        : "Optional"
      : reviewed[key] === mappings[key] ||
        [normalized(key), normalized(label)].includes(normalized(mappings[key]))
      ? "Matched"
      : "Needs review";
  const needsReview = engagementFields.filter((field) =>
    ["Needs review", "Missing"].includes(statusFor(field))
  );
  const optional = engagementFields.filter(
    (field) => statusFor(field) === "Optional"
  );
  const requiredMapped = ["engagementName", "workType", "clientName"].filter(
    (key) => mappings[key] || (key === "clientName" && mappings.clientCode)
  ).length;

  const matchesFilter = (field) =>
    (filter === "all" ||
      (filter === "review" &&
        ["Needs review", "Missing"].includes(statusFor(field))) ||
      (filter === "optional" && statusFor(field) === "Optional")) &&
    `${field[1]} ${mappings[field[0]] || ""}`
      .toLowerCase()
      .includes(search.toLowerCase());

  const renderRow = (field) => {
    const [key, originalLabel] = field,
      label = key.startsWith("risk") ? originalLabel.replace(/^Risk /, "") : originalLabel,
      status = statusFor(field),
      source = mappings[key];
    return (
      <tr
        key={key}
        className={
          status === "Needs review" || status === "Missing"
            ? "needs-review"
            : ""
        }
      >
        <th scope="row">
          {label}
          {isRequired(key) && <b className="mapping-required"> *</b>}
        </th>
        <td>
          <select
            value={source || ""}
            onChange={(event) => onChange(key, event.target.value)}
            aria-label={`Source column for ${label}`}
          >
            <option value="">
              {isRequired(key) ? "Select column" : "Not in file"}
            </option>
            {headers.map((header) => (
              <option
                key={header}
                disabled={used.includes(header) && source !== header}
              >
                {header}
              </option>
            ))}
          </select>
        </td>
        <td className="mapping-sample" title={sampleText(samples[source], key)}>
          {source ? sampleText(samples[source], key) || "No sample value" : "—"}
        </td>
        <td>
          <span
            className={`mapping-status ${
              status === "Matched"
                ? "is-matched"
                : status === "Optional"
                ? "is-optional"
                : "is-review"
            }`}
          >
            <i
              aria-hidden="true"
              className={`bx ${
                status === "Matched"
                  ? "bxs-check-circle"
                  : status === "Optional"
                  ? "bxs-minus-circle"
                  : "bxs-error"
              }`}
            />
            {status}
          </span>
          {status === "Needs review" && (
            <button
              type="button"
              className="mapping-confirm-match"
              onClick={() =>
                setReviewed((current) => ({ ...current, [key]: source }))
              }
              aria-label={`Confirm mapping for ${label}`}
            >
              Confirm
            </button>
          )}
        </td>
      </tr>
    );
  };
  const core = coreKeys.map((key) =>
    engagementFields.find((field) => field[0] === key)
  );
  const groups = [
    [
      "other",
      "Other optional fields",
      engagementFields.filter(
        ([key]) => !coreKeys.includes(key) && !key.startsWith("risk")
      ),
    ],
    [
      "risks",
      "Risk indicators",
      engagementFields.filter(([key]) => key.startsWith("risk")),
    ],
  ];
  return (
    <section className="engagement-import__workspace engagement-import__mapping-review mapping-review">
      <header className="mapping-review__heading">
        <div>
          <h2>Review column mappings</h2>
          <p>
            {aiUsed
              ? "Review the AI suggestions and match your file columns to PIERS fields."
              : "Match your file columns to PIERS fields."}
          </p>
        </div>
        <div className="mapping-review__summary">
          <span className="mapping-status is-matched">
            <i className="bx bxs-check-circle" aria-hidden="true" />
            {requiredMapped} required fields mapped
          </span>
          {needsReview.length > 0 && (
            <span className="mapping-status is-review">
              <i className="bx bxs-error" aria-hidden="true" />
              {needsReview.length} needs review
            </span>
          )}
        </div>
      </header>
      {warnings.map((warning, index) => (
        <div className="mapping-review__warning" role="status" key={index}>
          <i className="bx bxs-error" aria-hidden="true" />
          {warning}
        </div>
      ))}
      {needsReview.some((field) => statusFor(field) === "Needs review") && (
        <div className="mapping-review__warning" role="status">
          Some column names differ from the PIERS fields. Check the sample
          values and confirm the suggested matches.
        </div>
      )}
      <div className="mapping-review__toolbar">
        <div role="tablist" aria-label="Mapping filters">
          {[
            ["all", "All fields"],
            ["review", `Needs review (${needsReview.length})`],
            ["optional", `Unmapped optional (${optional.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filter === key}
              className={filter === key ? "is-active" : ""}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Search fields"
          aria-label="Search mapping fields"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="mapping-review__table-wrap">
        <table className="mapping-review__table">
          <thead>
            <tr>
              <th>PIERS field</th>
              <th>File column</th>
              <th>Sample from your file</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {core.filter(matchesFilter).map(renderRow)}
            {groups.flatMap(([, , fields]) =>
              fields.filter((field) => mappings[field[0]] && matchesFilter(field)).map(renderRow)
            )}
            {groups.map(([groupKey, label, fields]) => {
              const matches = fields.filter((field) => !mappings[field[0]] && matchesFilter(field));
              if (!matches.length) return null;
              if (filter !== "all" || search) return matches.map(renderRow);
              const unmapped = matches;
              return (
                <React.Fragment key={groupKey}>
                  {unmapped.length > 0 && (
                    <>
                      <tr className="mapping-review__group">
                        <th>
                          <button
                            type="button"
                            aria-expanded={Boolean(openGroups[groupKey])}
                            onClick={() =>
                              setOpenGroups((current) => ({
                                ...current,
                                [groupKey]: !current[groupKey],
                              }))
                            }
                          >
                            <i
                              className={`bx ${
                                openGroups[groupKey]
                                  ? "bx-chevron-down"
                                  : "bx-chevron-right"
                              }`}
                              aria-hidden="true"
                            />
                            {label} ({unmapped.length})
                          </button>
                        </th>
                        <td colSpan="3">
                          Not mapped — no values will be imported for these
                          fields.
                        </td>
                      </tr>
                      {openGroups[groupKey] && unmapped.map(renderRow)}
                    </>
                  )}
                </React.Fragment>
              );
            })}
            {!engagementFields.some(matchesFilter) && (
              <tr>
                <td colSpan="4">No fields match this filter.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <details className="mapping-review__excluded">
        <summary>
          <strong>File columns not imported ({excluded.length})</strong>
          <span>These columns will be ignored during import.</span>
          <b>Review</b>
        </summary>
        <ul>
          {excluded.map((header) => (
            <li key={header}>{header}</li>
          ))}
        </ul>
        {!excluded.length && <p>All source columns are mapped.</p>}
      </details>
      <footer className="mapping-review__footer">
        <Button type="button" color="link" onClick={onReset}>
          Reset mappings
        </Button>
        <label className="engagement-import__mapping-confirm">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => onConfirm(event.target.checked)}
          />{" "}
          I have reviewed these mappings.
        </label>
        <div>
          <Button type="button" outline onClick={onBack}>
            Back
          </Button>
          <Button
            type="button"
            color="primary"
            disabled={!canContinue}
            onClick={onContinue}
          >
            Validate &amp; preview{" "}
            <i className="bx bx-right-arrow-alt" aria-hidden="true" />
          </Button>
        </div>
      </footer>
    </section>
  );
}
